// ============================================
// Compute shader cache — content-addressed compiled WGSL
// ============================================
//
// Mirrors @engine/vfx's VFXShaderCache: the shader importer compiles a compute `.slang` and emits
// the WGSL into this content-addressed cache (id = hash of the WRAPPED source, so identical modules
// dedupe and unchanged sources skip recompiles); assets reference the id via importedData. At
// runtime the editor backing reads `/.cache/compute-shaders/<id>.wgsl` from the VFS, the player
// registers the build-emitted `compute-shaders.json` snapshot here.

/** VFS directory holding content-addressed compute shader files (`<id>.wgsl`). */
export const COMPUTE_SHADER_CACHE_DIR = '/.cache/compute-shaders';

const _cache = new Map<string, string>();

export function registerComputeShader(id: string, wgsl: string): void {
  _cache.set(id, wgsl);
}

export function registerComputeShaders(map: Record<string, string>): void {
  for (const id in map) _cache.set(id, map[id]);
}

export function hasComputeShader(id: string): boolean {
  return _cache.has(id);
}

export function getComputeShaderOrNull(id: string): string | undefined {
  return _cache.get(id);
}

export function clearComputeShaders(): void {
  _cache.clear();
}

/** VFS path a compute shader id resolves to in the editor's on-disk cache. */
export function computeShaderPath(id: string): string {
  return `${COMPUTE_SHADER_CACHE_DIR}/${id}.wgsl`;
}

/**
 * Content-addressed id for a compiled compute module — cyrb53 over the wrapped source, tagged with
 * the emitted format (same scheme as vfxShaderId; duplicated so render2d stays dependency-free).
 */
export function computeShaderId(wrappedSource: string, format: 'wgsl' = 'wgsl'): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < wrappedSource.length; i++) {
    const ch = wrappedSource.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hex = (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
  return `${format}_${hex}`;
}
